import { DestroyRef, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { catchError, filter, map, switchMap, take, tap } from "rxjs/operators";
import { AppNotificationService } from "src/app/core/services/app-notification.service";
import { HttpService } from "src/app/core/services/http.service";
import { LogoutService } from "src/app/core/services/logout.service";
import { handleErrorResponse } from "src/app/core/utils/error-handler";
import { AdminApiService } from "src/app/modules/admins/services/admin-api.service";
import { AppService } from "src/services/app.service";
import {
  AuthenticationRefreshRequest,
  RefreshState,
  UserAccessResponse,
} from "../models/login";
import { AccountService } from "./account.service";
import { LeaderElectionService } from "./leader-election.service";
import { TokenService } from "./token.service";

/**
 * Orchestrates authentication flow coordination between services
 */
@Injectable({ providedIn: "root" })
export class AuthCoordinatorService {
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(
    null,
  );
  private readonly refreshTokenInProgress = signal(false);

  public readonly refreshTokenSubject$ =
    this.refreshTokenSubject.asObservable();

  public readonly isRefreshTokenInProgress =
    this.refreshTokenInProgress.asReadonly();

  constructor(
    private appNotificationService: AppNotificationService,
    private leaderElectionService: LeaderElectionService,
    private adminApiService: AdminApiService,
    private accountService: AccountService,
    private logoutService: LogoutService,
    private tokenService: TokenService,
    private httpService: HttpService,
    private appService: AppService,
    private destroyRef: DestroyRef,
  ) {}

  /**
   * Initializes authentication flow coordination by setting up leader monitoring
   * and automatic token refresh mechanisms.
   */
  initialize(): void {
    this.setupLeaderMonitoring();
    this.setupLeaderAutoRefresh();
    this.setupLoggedInMonitoring();
    this.registerTabCloseHandler();
  }

  private registerTabCloseHandler(): void {
    window.addEventListener("beforeunload", this.resetLastRefresh.bind(this));
  }

  /**
   * Subscribes to leadership status changes and handles transitions when the
   * current tab gains or loses leadership.
   *
   * - Listens to `isLeader$` from `leaderElectionService`.
   * - Calls `handleLeadershipChange` whenever the leadership state changes.
   * - Ensures cleanup when the component is destroyed.
   */
  private setupLeaderMonitoring(): void {
    this.leaderElectionService.isLeader$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isLeader) => this.handleLeadershipChange(isLeader));
  }

  /**
   * Sets up monitoring for login state changes across tabs.
   *
   * This method listens to the `loggedInObservable$` from `AppService`, which emits in the following scenarios:
   * - After a successful login via the standard authentication flow.
   * - Upon receiving login-related messages from the BroadcastChannel (e.g., `TOKEN_FROM_ANOTHER_TAB`, `TOKENS_UPDATED`).
   *
   * When triggered, it re-evaluates whether the current tab is the elected leader
   * (using `LeaderElectionService.isLeader`) and executes `handleLeadershipChange` accordingly.
   *
   * This ensures that the leader tab reacts promptly to cross-tab login events,
   * maintaining proper session and leadership coordination.
   *
   * Cleanup is automatically handled on component/service destruction via `takeUntilDestroyed`.
   */
  private setupLoggedInMonitoring(): void {
    this.appService
      .getLoggedInObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleLeadershipChange(this.leaderElectionService.isLeader);
      });
  }

  /**
   * Sets up automatic authentication token refresh when the current tab is
   * the elected leader.
   *
   * - Triggers a token refresh if the tab is a leader and conditions allow.
   * - Broadcasts refresh state (`SKIPPED`, `STARTED`, `SUCCESS`, `FAILED`) for synchronization.
   * - Ensures the refresh process stops if leadership is lost.
   */
  private setupLeaderAutoRefresh(): void {
    this.leaderElectionService.isLeader$
      .pipe(
        filter((leader) => leader), // Proceed only if leader
        tap(() => {
          const canRestoreSession =
            this.accountService.canRestoreSessionFromRefreshToken();
          if (!canRestoreSession) {
            this.broadcastRefreshState(RefreshState.SKIPPED);
          }
        }),
        filter(() => this.accountService.canRestoreSessionFromRefreshToken()), // Restore session check
        tap(() => this.broadcastRefreshState(RefreshState.STARTED)), // Notify other tabs
        switchMap(() => this.refreshTokenIfNeeded$()), // Refresh token
        switchMap(() =>
          this.adminApiService.getCurrentAuthenticatedAdminData(),
        ), // Admin User Data
        takeUntilDestroyed(this.destroyRef), // Ensure cleanup
      )
      .subscribe({
        next: () => {
          this.accountService.isLoggedIn() &&
            this.broadcastRefreshState(RefreshState.SUCCESS);
        },
        error: (err) => {
          this.broadcastRefreshState(RefreshState.FAILED);
          handleErrorResponse(err, this.appNotificationService);
        },
      });
  }

  /**
   * Attempts to refresh authentication tokens if conditions are met.
   *
   * @returns {Observable<UserAccessResponse | null>} An observable emitting the token refresh result.
   */
  private refreshTokenIfNeeded$(): Observable<UserAccessResponse | null> {
    const refreshToken = this.accountService.getRefreshTokenFromCookie();
    if (!refreshToken || !this.tokenService.isRefreshIntervalElapsed()) {
      this.broadcastRefreshState(RefreshState.SKIPPED);
      return of(null);
    }

    if (this.isRefreshTokenInProgress()) {
      // If a refresh is already in progress, wait for it to complete
      return this.refreshTokenSubject$.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((newToken) => of({ token: newToken } as UserAccessResponse)),
      );
    }

    // Set the refresh flag
    this.refreshTokenInProgress.set(true);
    this.refreshTokenSubject.next(null);

    return this.performTokenRefresh({ refresh_token: refreshToken }).pipe(
      tap((response) => {
        this.refreshTokenSubject.next(response.token);
        this.refreshTokenInProgress.set(false);
      }),
    );
  }

  /**
   * Handles transitions when the current tab gains or loses leadership.
   *
   * @param {boolean} isLeader - Indicates whether the current tab is the leader.
   */
  private handleLeadershipChange(isLeader: boolean): void {
    if (isLeader) {
      this.handleLeaderPromotion();
    } else {
      this.handleLeaderDemotion();
    }
  }

  /**
   * Executes actions when the current tab becomes the leader, ensuring token validity
   * and scheduling refresh if needed.
   */
  private handleLeaderPromotion(): void {
    const token = this.accountService.getUserAccessToken();
    if (token && this.tokenService.isValid(token)) {
      const expiresIn = this.tokenService.getExpirationSeconds(token);
      this.scheduleTokenRefresh(expiresIn);
    }
  }

  /**
   * Executes cleanup actions when the current tab loses leadership,
   * stopping any scheduled token refresh.
   */
  private handleLeaderDemotion(): void {
    this.tokenService.clearRefreshTimeout();
    this.broadcastRefreshState(RefreshState.STALE);
  }

  /**
   * Broadcasts a refresh state change to synchronize authentication across tabs.
   * This should only be triggered when the leader explicitly takes responsibility
   * for token refresh, ensuring that automatic refreshes do not broadcast state changes.
   *
   * @param {RefreshState} state - The new refresh state to be broadcasted.
   */
  private broadcastRefreshState(state: RefreshState): void {
    this.leaderElectionService.broadcastRefreshState(state);
  }

  /**
   * Schedules a token refresh operation to run after a specified delay.
   *
   * @param {number} expiresIn - The time (in seconds) until the next refresh.
   */
  scheduleTokenRefresh(expiresIn: number): void {
    this.tokenService.scheduleRefresh(
      () => this.refreshTokenIfNeeded$().subscribe(),
      expiresIn,
    );
  }

  /**
   * Executes the token refresh HTTP request
   * @param request Refresh token request payload
   * @returns Observable of refresh operation response
   */
  private performTokenRefresh(
    request: AuthenticationRefreshRequest,
  ): Observable<UserAccessResponse> {
    return this.httpService.post("auth/refresh", request).pipe(
      map(({ data }) => this.handleRefreshSuccess(data)),
      catchError((error) => this.handleRefreshError(error)),
    );
  }

  /**
   * Handles successful token refresh response
   * @param response Server response data
   * @returns Processed response data
   */
  private handleRefreshSuccess(data: UserAccessResponse): UserAccessResponse {
    if (data) {
      this.accountService.setTokens(data);
      this.tokenService.updateLastRefresh();
      this.scheduleTokenRefresh(data.expires);
    }
    return data;
  }

  /**
   * Handles token refresh errors
   * @param error Error object from HTTP call
   * @throws The original error
   */
  private handleRefreshError(error: any): Observable<never> {
    console.error("Token refresh failed, logout!", error);
    this.refreshTokenInProgress.set(false);
    this.logoutService.logout({ redirectToReturnUrl: true }).subscribe();
    return throwError(() => error);
  }

  private resetLastRefresh(): void {
    this.leaderElectionService.isLeader && this.tokenService.resetLastRefresh();
  }
}
